import React, { useEffect, useState } from 'react'
import useI18n from '@/hooks/useI18n'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * The two largest meaningful units: day/hour above a day, hour/minute above an hour,
 * minute/second below it. Values are clamped at zero so an expired market reads 00 : 00
 * rather than going negative.
 */
function getUnits(remaining) {
  const left = Math.max(remaining, 0)

  if (left >= DAY) {
    return [
      { key: 'day', value: Math.floor(left / DAY) },
      { key: 'hour', value: Math.floor((left % DAY) / HOUR) },
    ]
  }

  if (left >= HOUR) {
    return [
      { key: 'hour', value: Math.floor(left / HOUR) },
      { key: 'min', value: Math.floor((left % HOUR) / MINUTE) },
    ]
  }

  return [
    { key: 'min', value: Math.floor(left / MINUTE) },
    { key: 'sec', value: Math.floor((left % MINUTE) / SECOND) },
  ]
}

const EventCountdown = ({ endDate, className = '' }) => {
  const i18n = useI18n()
  const endTS = Date.parse(endDate ?? '')
  // Filled in by the effect on the client: the market page is server-rendered, and computing
  // the digits during render would ship server-clock values the client immediately disagrees with.
  const [remaining, setRemaining] = useState(null)

  useEffect(() => {
    if (!Number.isFinite(endTS)) {
      setRemaining(null)
      return undefined
    }

    setRemaining(endTS - Date.now())
    const tick = setInterval(() => setRemaining(endTS - Date.now()), SECOND)
    return () => clearInterval(tick)
  }, [endTS])

  if (remaining === null) return null

  // Urgency reads off the digits alone; the unit labels stay neutral.
  let digitColor = 'text-Neutral-6'
  if (remaining < MINUTE) {
    digitColor = 'text-negative-4'
  } else if (remaining <= 5 * MINUTE) {
    digitColor = 'text-warning-4'
  }

  return (
    <div className={`flex items-center gap-2 text-center ${className}`}>
      {getUnits(remaining).map(({ key, value }) => (
        <div key={key} className="flex flex-col items-center gap-1">
          <span className={`text-2xl font-semibold leading-[1.33] tabular-nums ${digitColor}`}>
            {String(value).padStart(2, '0')}
          </span>
          <span className="text-xs leading-[1.2] uppercase text-Neutral-4">
            {i18n(`question_card.countdown.${key}`)}
          </span>
        </div>
      ))}
    </div>
  )
}

export default EventCountdown
